import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivityReportChartItem } from '../../../pages/activity-reports/models/activity-reports.models';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BasicHeatmapChartService {
  private isBrowser: boolean;
  private chart: any | null = null;

  private readonly DISCHARGE_OFFSET = 10000; // discharge limit (%) offset
  private readonly CHARGE_LIMIT_OFFSET = 11000; // charge limit (%) offset

  private readonly AC_OFFSET = 20000;
  private readonly AC_NULL = this.AC_OFFSET - 1; // 19999
  private readonly AC_OFF = this.AC_OFFSET;     // 20000
  private readonly AC_ON = this.AC_OFFSET + 1; // 20001

  private readonly CHARGE_OFFSET = 30000;
  private readonly CHARGE_FROM_GRID_OFFSET = 40000;

  private readonly DISCHARGE_KWH_OFFSET = 50000;
  private readonly SOLAR_OFFSET = 60000;

  // Profit offsets — positive/negative profit stored as offset+scaledAbsValue
  // so shadeIntensity makes MORE profit = MORE vivid (values near `to` = vivid)
  private readonly PROFIT_POS_OFFSET = 70000; // positive profit: y = 70000 + absVal*scale
  private readonly PROFIT_NEG_OFFSET = 80000; // negative profit: y = 80000 + absVal*scale

  private readonly CONSUMPTION_OFFSET = 90000;

  // Charge state (on/off/null) — discrete values like AC states
  private readonly CHARGE_STATE_OFFSET = 22000;
  private readonly CHARGE_STATE_OFF = this.CHARGE_STATE_OFFSET;    
  private readonly CHARGE_STATE_ON = this.CHARGE_STATE_OFFSET + 1;  

  // Discharge state (on/off/null) — discrete values like AC states
  private readonly DISCHARGE_STATE_OFFSET = 23000;
  private readonly DISCHARGE_STATE_OFF = this.DISCHARGE_STATE_OFFSET;    // 23000
  private readonly DISCHARGE_STATE_ON = this.DISCHARGE_STATE_OFFSET + 1; // 23001

  private readonly NO_DATA_Y = this.AC_NULL; 

  private readonly ZERO_COLOR = '#e1e1e1';       

  private readonly CHARGE_MIN_COLOR = '#ffe0f0';
  private readonly CHARGE_MAX_COLOR = '#be185d';

  private readonly DISCHARGE_MIN_COLOR = '#ddd6fe';
  private readonly DISCHARGE_MAX_COLOR = '#4c1d95';

  private readonly CHARGE_FROM_GRID_MIN_COLOR = '#ffebe7';
  private readonly CHARGE_FROM_GRID_MAX_COLOR = '#d31510';

  private readonly SOLAR_MIN_COLOR = '#ffeccc';
  private readonly SOLAR_MAX_COLOR = '#f68511';

  private readonly CONSUMPTION_MIN_COLOR = '#f3e8ff';
  private readonly CONSUMPTION_MAX_COLOR = '#780078';

  private lastItems: ActivityReportChartItem[] = [];
  private langSub?: Subscription;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private translate: TranslateService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser) {
      const storedLang = localStorage.getItem('app_locale');
      this.translate.setDefaultLang(storedLang || 'en');
      this.translate.use(storedLang || 'en');
    } else {
      this.translate.setDefaultLang('en');
      this.translate.use('en');
    }

    this.langSub = this.translate.onLangChange.subscribe(() => {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
        this.loadChart(this.lastItems);
      }
    });
  }

  private currentLocale(): string {
    return this.translate.currentLang || (this.isBrowser ? (localStorage.getItem('app_locale') || 'en') : 'en');
  }

  private formatPercent(val: number): string {
    const locale = this.currentLocale();
    const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    return locale.startsWith('da') ? `${nf.format(val)} %` : `${nf.format(val)}%`;
  }

  private formatKwh(val: number): string {
    const locale = this.currentLocale();
    const nf = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${nf.format(val)} kWh`;
  }

  private formatKr(val: number): string {
    const locale = this.currentLocale();
    const nf = new Intl.NumberFormat(locale, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    return `${nf.format(val)} kr.`;
  }

  private safeNumber(val: any): number {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  }

  private clamp(n: number, min: number, max: number): number {
    return Math.min(Math.max(n, min), max);
  }

  private safeSoc(val: any): number {
    if (val == null) return 0;
    if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
    return this.safeNumber(val.value ?? val._value);
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    const cleaned = hex.replace('#', '');
    const bigint = parseInt(cleaned, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  }

  private lerpColor(startHex: string, endHex: string, t: number): string {
    const s = this.hexToRgb(startHex);
    const e = this.hexToRgb(endHex);
    const r = Math.round(s.r + (e.r - s.r) * t);
    const g = Math.round(s.g + (e.g - s.g) * t);
    const b = Math.round(s.b + (e.b - s.b) * t);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  // ---------- Capacity based scaling (Charge / Discharge kWh) ----------

  private getBatteryCapacityKwh(items: ActivityReportChartItem[]): number {
    const first = items?.[0] as any;
    const cap = this.safeNumber(first?.totalBatteryCapacityKwh);
    return cap > 0 ? cap : 1;
  }

  private toCapacityPct(kwh: number, capacityKwh: number): number {
    if (!capacityKwh || capacityKwh <= 0) return 0;
    const pct = (this.safeNumber(kwh) / capacityKwh) * 100;
    return this.clamp(pct, 0, 100);
  }

  private buildCapacityRanges(
    offset: number,
    zeroColor: string,
    startHex: string,
    endHex: string,
    label: string,
    steps: number = 10,
    maxPct: number = 100
  ): any[] {
    // pct == 0  => handled by zero sentinel range (offset - 0.5 to offset + 0.5) in colorScale
    // pct > 0   => gradient startHex -> endHex, normalized against actual maxPct in the dataset
    const effectiveMax = Math.max(1, maxPct);
    const minVal = offset + 0.5;
    const maxVal = offset + effectiveMax;
    const span = maxVal - minVal;
    const stepSize = span / steps;

    const ranges: any[] = [];
    for (let i = 0; i < steps; i++) {
      const from = minVal + i * stepSize;
      const to = i === steps - 1 ? maxVal : minVal + (i + 1) * stepSize;

      const fromPct = Math.max(0, from - offset);
      const toPct = Math.min(effectiveMax, to - offset);
      const midPct = (fromPct + toPct) / 2;

      const t = this.normalize1to100(0, effectiveMax, midPct) / 100;
      const color = this.lerpColor(startHex, endHex, t);

      ranges.push({
        from,
        to,
        color,
        name: `${label}: ${this.formatPercent(fromPct)} - ${this.formatPercent(toPct)}`
      });
    }

    return ranges;
  }

  // ---------- kWh-based ranges (Solar / Charge from grid) ----------

  private buildKwhRanges(
    offset: number,
    maxKwh: number,
    startHex: string,
    endHex: string,
    label: string,
    steps: number = 10
  ): any[] {
    const effectiveMax = Math.max(1, maxKwh);
    const minVal = offset + 0.000001;
    const maxVal = offset + effectiveMax;
    const span = maxVal - minVal;
    const stepSize = span / steps;

    const ranges: any[] = [];
    for (let i = 0; i < steps; i++) {
      const from = minVal + i * stepSize;
      const to = i === steps - 1 ? maxVal : minVal + (i + 1) * stepSize;

      const fromKwh = from - offset;
      const toKwh = to - offset;
      const midKwh = (fromKwh + toKwh) / 2;

      const t = this.normalize1to100(0, effectiveMax, midKwh) / 100;
      const color = this.lerpColor(startHex, endHex, t);

      ranges.push({
        from,
        to,
        color,
        name: `${label}: ${this.formatKwh(fromKwh)} - ${this.formatKwh(toKwh)}`
      });
    }

    return ranges;
  }

  // ---------- Existing ranges (Soc / Discharge limit) ----------

  private buildChargeLimitColorRanges(steps: number = 10): any[] {
    const startColor = '#dbeafe';
    const endColor = '#1d4ed8';
    const offset = this.CHARGE_LIMIT_OFFSET;

    const minVal = offset;
    const maxVal = offset + 100;
    const totalSpan = maxVal - minVal;
    const stepSize = totalSpan / steps;

    const ranges: any[] = [];
    for (let i = 0; i < steps; i++) {
      const from = minVal + i * stepSize;
      const to = i === steps - 1 ? maxVal : minVal + (i + 1) * stepSize;

      const fromPercent = Math.max(0, from - offset);
      const toPercent = Math.min(100, to - offset);
      const midPercent = (fromPercent + toPercent) / 2;

      const t = this.normalize1to100(0, 100, midPercent) / 100;
      const color = this.lerpColor(startColor, endColor, t);

      ranges.push({
        from,
        to,
        color,
        name: `${this.formatPercent(fromPercent)} - ${this.formatPercent(toPercent)}`
      });
    }
    return ranges;
  }

  private buildDischargeLimitColorRanges(steps: number = 10): any[] {
    const startColor = '#cef8e0';
    const endColor = '#007a4d';
    const offset = this.DISCHARGE_OFFSET;

    const minVal = offset;
    const maxVal = offset + 100;
    const totalSpan = maxVal - minVal;
    const stepSize = totalSpan / steps;

    const ranges: any[] = [];
    for (let i = 0; i < steps; i++) {
      const from = minVal + i * stepSize;
      const to = i === steps - 1 ? maxVal : minVal + (i + 1) * stepSize;

      const fromPercent = Math.max(0, from - offset);
      const toPercent = Math.min(100, to - offset);
      const midPercent = (fromPercent + toPercent) / 2;

      const t = this.normalize1to100(0, 100, midPercent) / 100;
      const color = this.lerpColor(startColor, endColor, t);

      ranges.push({
        from,
        to,
        color,
        name: `${this.formatPercent(fromPercent)} - ${this.formatPercent(toPercent)}`
      });
    }
    return ranges;
  }

  private buildSocColorRanges(steps: number = 10): any[] {
    const startColor = '#cef8e0';
    const endColor = '#007a4d';
    const offset = 1;

    const minVal = offset;
    const maxVal = offset + 100;
    const totalSpan = maxVal - minVal;
    const stepSize = totalSpan / steps;

    const ranges: any[] = [];
    for (let i = 0; i < steps; i++) {
      const from = minVal + i * stepSize;
      const to = i === steps - 1 ? maxVal : minVal + (i + 1) * stepSize;

      const fromPercent = Math.max(0, from - offset);
      const toPercent = Math.min(100, to - offset);
      const midPercent = (fromPercent + toPercent) / 2;

      const t = this.normalize1to100(0, 100, midPercent) / 100;
      const color = this.lerpColor(startColor, endColor, t);

      ranges.push({
        from,
        to,
        color,
        name: `${this.formatPercent(fromPercent)} - ${this.formatPercent(toPercent)}`
      });
    }
    return ranges;
  }

  private getSocColor(soc: number) {
    const socRanges = this.buildSocColorRanges(10);
    const range = socRanges.find(r => soc >= r.from && soc < r.to);
    return range ? range.color : '#94a3b8';
  }

  async loadChart(items: ActivityReportChartItem[]): Promise<void> {
    if (!this.isBrowser) return;

    try {
      const ApexCharts = (await import('apexcharts')).default;

      const host = document.querySelector('#basic_heatmap_chart') as HTMLElement | null;
      if (!host) {
        console.warn('BasicHeatmapChartService: element #basic_heatmap_chart not found');
        return;
      }

      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      const sorted = [...(items ?? [])].sort((a, b) => a.hour - b.hour);
      this.lastItems = sorted;

      const categories = sorted.map(i => `${String(i.hour).padStart(2, '0')}:00`);

      const makePoint = (hour: number, raw: any) => ({
        x: `${String(hour).padStart(2, '0')}:00`,
        y: this.safeNumber(raw)
      });

      const chargedLabel = await firstValueFrom(this.translate.get('Charged (kWh)'));
      const dischargedLabel = await firstValueFrom(this.translate.get('Discharged (kWh)'));
      const solarProductionLabel = await firstValueFrom(this.translate.get('Solar production (kWh)'));
      const title = await firstValueFrom(this.translate.get('Activity metrics by hour'));
      const chargedFromGridLabel = await firstValueFrom(this.translate.get('Charge from grid (kWh)'));
      const profitLabel = await firstValueFrom(this.translate.get('Profit (kr.)'));
      const acChargeLabel = await firstValueFrom(this.translate.get('AC charge (on/off)'));
      const batterySocLabel = await firstValueFrom(this.translate.get('Battery Soc(%)'));
      const dischargeLimitLabel = await firstValueFrom(this.translate.get('Discharge limit (%)'));
      const chargeLimitLabel = await firstValueFrom(this.translate.get('Charge limit (%)'));
      const consumptionLabel = await firstValueFrom(this.translate.get('Consumption'));
      const chargeStateLabel = await firstValueFrom(this.translate.get('Charge / Discharge state'));
      const tChargeOn = await firstValueFrom(this.translate.get('Charge'));
      const tDischargeOn = await firstValueFrom(this.translate.get('Discharge'));

      const tAcOn = await firstValueFrom(this.translate.get('AC On'));
      const tAcOff = await firstValueFrom(this.translate.get('AC Off'));
      const tAcNA = await firstValueFrom(this.translate.get('AC N/A'));

      const tNegative = await firstValueFrom(this.translate.get('Negative'));
      const tZero = await firstValueFrom(this.translate.get('Zero'));
      const tLow = await firstValueFrom(this.translate.get('Low'));
      const tMedium = await firstValueFrom(this.translate.get('Medium'));
      const tHigh = await firstValueFrom(this.translate.get('High'));
      const tVeryHigh = await firstValueFrom(this.translate.get('Very high'));

      const tOn = await firstValueFrom(this.translate.get('On'));
      const tOff = await firstValueFrom(this.translate.get('Off'));
      const tNA = await firstValueFrom(this.translate.get('N/A'));

      const capacityKwh = this.getBatteryCapacityKwh(sorted);

      // An hour has no data only when acCharge is null AND all other meaningful fields are empty/zero.
      const noData = (i: ActivityReportChartItem) =>
        i.acCharge == null &&
        (i.chargedKwh ?? 0) === 0 &&
        (i.dischargedKwh ?? 0) === 0 &&
        (i.solarProductionKwh ?? 0) === 0 &&
        (i.chargedFromGridKwh ?? 0) === 0 &&
        i.soc == null;

      // Maxes for other series
      const maxChargeFromGrid = Math.max(0, ...sorted.map(i => this.safeNumber(i.chargedFromGridKwh)));
      const maxSolarKwh = Math.max(0, ...sorted.map(i => this.safeNumber(i.solarProductionKwh)));
      const maxConsumptionKwh = Math.max(0, ...sorted.map(i => this.safeNumber(i.consumptionKwh)));

      const dataItems = sorted.filter(i => !noData(i));
      const FIXED_PROFIT_MAX = 3; // Fixed at 3 kr to ensure consistent colors across all days
      const profitAbsMax = FIXED_PROFIT_MAX;
      const profitScale = 100 / profitAbsMax;

      const toProfitY = (p: number): number => {
        if (p > 0.000001) return this.PROFIT_POS_OFFSET + p * profitScale;
        if (p < -0.000001) return this.PROFIT_NEG_OFFSET + (-p) * profitScale;
        return 0; // exactly zero → ZERO_COLOR range
      };

      // ApexCharts renders series bottom→top, so row 1 (top) must be LAST in the array.
      // Desired top→bottom: chargeDischargeState | ac | dischargeLimit | soc | solar | chargeFromGrid | charged | discharged | consumption | profit

      const series = [
        {
          name: profitLabel,
          data: sorted.map(i => ({
            x: `${String(i.hour).padStart(2, '0')}:00`,
            y: noData(i) ? this.NO_DATA_Y : toProfitY(this.safeNumber(i.totalPrice))
          }))
        },
        {
          name: consumptionLabel,
          data: sorted.map(i => ({
            x: `${String(i.hour).padStart(2, '0')}:00`,
            y: noData(i) ? this.NO_DATA_Y : this.safeNumber(i.consumptionKwh) + this.CONSUMPTION_OFFSET
          }))
        },
        {
          name: dischargedLabel,
          data: sorted.map(i => {
            if (noData(i)) return { x: `${String(i.hour).padStart(2, '0')}:00`, y: this.NO_DATA_Y };
            const pct = this.toCapacityPct(this.safeNumber(i.dischargedKwh), capacityKwh);
            return { x: `${String(i.hour).padStart(2, '0')}:00`, y: pct + this.DISCHARGE_KWH_OFFSET };
          })
        },
        {
          name: chargedLabel,
          data: sorted.map(i => {
            if (noData(i)) return { x: `${String(i.hour).padStart(2, '0')}:00`, y: this.NO_DATA_Y };
            const pct = this.toCapacityPct(this.safeNumber(i.chargedKwh), capacityKwh);
            return { x: `${String(i.hour).padStart(2, '0')}:00`, y: pct + this.CHARGE_OFFSET };
          })
        },
        {
          name: chargedFromGridLabel,
          data: sorted.map(i => ({
            x: `${String(i.hour).padStart(2, '0')}:00`,
            y: noData(i) ? this.NO_DATA_Y : this.safeNumber(i.chargedFromGridKwh) + this.CHARGE_FROM_GRID_OFFSET
          }))
        },
        {
          name: solarProductionLabel,
          data: sorted.map(i => ({
            x: `${String(i.hour).padStart(2, '0')}:00`,
            y: noData(i) ? this.NO_DATA_Y : this.safeNumber(i.solarProductionKwh) + this.SOLAR_OFFSET
          }))
        },
        {
          name: batterySocLabel,
          data: sorted.map(i => {
            if (noData(i)) return { x: `${String(i.hour).padStart(2, '0')}:00`, y: this.NO_DATA_Y };
            const socValue = this.safeSoc(i.soc);
            return { x: `${String(i.hour).padStart(2, '0')}:00`, y: socValue, color: this.getSocColor(socValue) };
          })
        },
        {
          name: chargeLimitLabel,
          data: sorted.map(i => ({
            x: `${String(i.hour).padStart(2, '0')}:00`,
            y: noData(i) || i.chargeLimitPercentage == null ? this.NO_DATA_Y : this.safeNumber(i.chargeLimitPercentage) + this.CHARGE_LIMIT_OFFSET
          }))
        },
        {
          name: dischargeLimitLabel,
          data: sorted.map(i => ({
            x: `${String(i.hour).padStart(2, '0')}:00`,
            y: noData(i) ? this.NO_DATA_Y : this.safeNumber(i.dischargeLimitPercentage) + this.DISCHARGE_OFFSET
          }))
        },
        {
          name: acChargeLabel,
          data: sorted.map(i => ({
            x: `${String(i.hour).padStart(2, '0')}:00`,
            y: (i.acCharge == null)
              ? this.AC_NULL
              : (i.acCharge === true ? this.AC_ON : this.AC_OFF)
          }))
        },
        {
          name: chargeStateLabel,
          data: sorted.map(i => {
            const x = `${String(i.hour).padStart(2, '0')}:00`;
            if (i.chargeIsSet === true)    return { x, y: this.CHARGE_STATE_ON };
            if (i.dischargeIsSet === true) return { x, y: this.DISCHARGE_STATE_ON };
            if (i.chargeIsSet === false || i.dischargeIsSet === false) return { x, y: this.CHARGE_STATE_OFF };
            return { x, y: this.AC_NULL };
          })
        }
      ];

      const seriesKind: Array<'profit' | 'ac' | 'soc' | 'dischargeLimit' | 'chargeLimit' | 'charge' | 'chargeGrid' | 'consumption' | 'dischargeKwh' | 'solar' | 'chargeDischargeState'> = [
        'profit', 'consumption', 'dischargeKwh', 'charge', 'chargeGrid', 'solar', 'soc', 'chargeLimit', 'dischargeLimit', 'ac', 'chargeDischargeState'
      ];

      const maxChargedPct = Math.max(1, ...sorted.map(i => this.toCapacityPct(this.safeNumber(i.chargedKwh), capacityKwh)));
      const maxDischargedPct = Math.max(1, ...sorted.map(i => this.toCapacityPct(this.safeNumber(i.dischargedKwh), capacityKwh)));

      const chargePctRanges = this.buildCapacityRanges(
        this.CHARGE_OFFSET,
        this.ZERO_COLOR,
        this.CHARGE_MIN_COLOR,
        this.CHARGE_MAX_COLOR,
        chargedLabel,
        10,
        maxChargedPct
      );

      const dischargePctRanges = this.buildCapacityRanges(
        this.DISCHARGE_KWH_OFFSET,
        this.ZERO_COLOR,
        this.DISCHARGE_MIN_COLOR,
        this.DISCHARGE_MAX_COLOR,
        dischargedLabel,
        10,
        maxDischargedPct
      );

      const dischargeLimitRanges = this.buildDischargeLimitColorRanges(10);
      const chargeLimitRanges = this.buildChargeLimitColorRanges(10);
      const socRanges = this.buildSocColorRanges(10);

      const chargeFromGridRanges = this.buildKwhRanges(
        this.CHARGE_FROM_GRID_OFFSET,
        maxChargeFromGrid,
        this.CHARGE_FROM_GRID_MIN_COLOR,
        this.CHARGE_FROM_GRID_MAX_COLOR,
        chargedFromGridLabel,
        10
      );

      const solarRanges = this.buildKwhRanges(
        this.SOLAR_OFFSET,
        maxSolarKwh,
        this.SOLAR_MIN_COLOR,
        this.SOLAR_MAX_COLOR,
        solarProductionLabel,
        10
      );

      // Profit gradient ranges reuse buildCapacityRanges (offset + 0..100 units)
      // Positive: vivid green-400 → dark green-900 (higher profit = more vivid toward `to`)
      const profitPosRanges = this.buildCapacityRanges(
        this.PROFIT_POS_OFFSET, this.ZERO_COLOR, '#dbfc6e', '#577400', profitLabel, 20
      );
      // Negative: light red → dark red-900 (larger loss = closer to `to` = more vivid/darker)
      const profitNegRanges = this.buildCapacityRanges(
        this.PROFIT_NEG_OFFSET, this.ZERO_COLOR, '#ffebe7', '#b40000', profitLabel, 20
      );

      // maxScale: charge/discharge percent => offset+100
      const chargeMaxScaled = this.CHARGE_OFFSET + 100 + 1;
      const dischargeMaxScaled = this.DISCHARGE_KWH_OFFSET + 100 + 1;

      const chargeFromGridMaxScaled = this.CHARGE_FROM_GRID_OFFSET + Math.max(1, maxChargeFromGrid) + 1;
      const solarMaxScaled = this.SOLAR_OFFSET + Math.max(1, maxSolarKwh) + 1;
      const consumptionMaxScaled = this.CONSUMPTION_OFFSET + Math.max(1, maxConsumptionKwh) + 1;

      const consumptionRanges = this.buildKwhRanges(
        this.CONSUMPTION_OFFSET,
        maxConsumptionKwh,
        this.CONSUMPTION_MIN_COLOR,
        this.CONSUMPTION_MAX_COLOR,
        'Consumption',
        10
      );

      const dischargeLimitMax = this.DISCHARGE_OFFSET + 100 + 1;
      const chargeLimitMax = this.CHARGE_LIMIT_OFFSET + 100 + 1;

      const maxScale = Math.max(
        dischargeLimitMax,
        chargeLimitMax,
        this.AC_ON + 1,
        chargeMaxScaled,
        dischargeMaxScaled,
        chargeFromGridMaxScaled,
        solarMaxScaled,
        consumptionMaxScaled,
        this.PROFIT_NEG_OFFSET + 101,
        this.DISCHARGE_STATE_ON + 1
      );

      const options: any = {
        series,
        chart: { height: 350, type: 'heatmap', toolbar: { show: true } },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.8,
            distributed: false,
            colorScale: {
              ranges: [
                // AC states (AC_NULL also serves as the no-data sentinel for all series)
                { from: this.AC_ON - 0.1, to: this.AC_ON + 0.1, color: '#FFEB3B', name: tAcOn },
                { from: this.AC_OFF - 0.1, to: this.AC_OFF + 0.1, color: '#8BC34A', name: tAcOff },
                { from: this.AC_NULL - 0.1, to: this.AC_NULL + 0.1, color: '#cbd5e1', name: tAcNA },

                // Charge from grid: 0 => ZERO_COLOR, >0 => gradient
                { from: this.CHARGE_FROM_GRID_OFFSET - 0.000001, to: this.CHARGE_FROM_GRID_OFFSET + 0.000001, color: this.ZERO_COLOR, name: chargedFromGridLabel },
                ...chargeFromGridRanges,

                // Charge: pct==0 => ZERO_COLOR, pct>0 => gradient (gradient starts at offset+0.5)
                { from: this.CHARGE_OFFSET - 0.5, to: this.CHARGE_OFFSET + 0.5, color: this.ZERO_COLOR, name: chargedLabel },
                ...chargePctRanges,

                // Discharge: pct==0 => ZERO_COLOR, pct>0 => gradient
                { from: this.DISCHARGE_KWH_OFFSET - 0.5, to: this.DISCHARGE_KWH_OFFSET + 0.5, color: this.ZERO_COLOR, name: dischargedLabel },
                ...dischargePctRanges,

                // Solar: 0 => ZERO_COLOR, >0 => gradient
                { from: this.SOLAR_OFFSET - 0.000001, to: this.SOLAR_OFFSET + 0.000001, color: this.ZERO_COLOR, name: solarProductionLabel },
                ...solarRanges,

                // Consumption: 0 => ZERO_COLOR, >0 => gradient
                { from: this.CONSUMPTION_OFFSET - 0.000001, to: this.CONSUMPTION_OFFSET + 0.000001, color: this.ZERO_COLOR, name: consumptionLabel },
                ...consumptionRanges,

                // Profit: offset-based so shadeIntensity direction is always correct.
                // Zero profit → ZERO_COLOR. Positive/negative use separate offsets.
                { from: -0.5, to: 0.5, color: this.ZERO_COLOR, name: tZero },
                // Cover very small profit values that fall below the gradient start (offset+0.5)
                { from: this.PROFIT_POS_OFFSET, to: this.PROFIT_POS_OFFSET + 0.5, color: '#dbfc6e', name: profitLabel },
                ...profitPosRanges,
                { from: this.PROFIT_NEG_OFFSET, to: this.PROFIT_NEG_OFFSET + 0.5, color: '#ffebe7', name: profitLabel },
                ...profitNegRanges,
                // Extended ranges for values beyond 3 kr - they get the max color (darkest shade).
                // Upper bound kept below CONSUMPTION_OFFSET (90000) to avoid overriding the consumption zero sentinel.
                { from: this.PROFIT_POS_OFFSET + 100, to: this.PROFIT_POS_OFFSET + 10000, color: '#577400', name: `${profitLabel}: ${tVeryHigh}` },
                { from: this.PROFIT_NEG_OFFSET + 100, to: this.CONSUMPTION_OFFSET - 0.5, color: '#b40000', name: `${profitLabel}: ${tVeryHigh} loss` },

                // Discharge limit + Charge limit + SOC ranges
                ...dischargeLimitRanges,
                ...chargeLimitRanges,
                ...socRanges,

                // Charge / Discharge state: charge-on=green, discharge-on=orange, off=dark, null→AC_NULL gray
                { from: this.CHARGE_STATE_ON - 0.1,    to: this.CHARGE_STATE_ON + 0.1,    color: '#be185d', name: tChargeOn },
                { from: this.DISCHARGE_STATE_ON - 0.1, to: this.DISCHARGE_STATE_ON + 0.1, color: '#4c1d95', name: tDischargeOn },
                { from: this.CHARGE_STATE_OFF - 0.1,   to: this.CHARGE_STATE_OFF + 0.1,   color: '#374151', name: tOff }
              ],
              min: -100000,
              max: maxScale
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val: any, opts: any) => {
            const n = Number(val);
            const sIdx: number = opts?.seriesIndex ?? -1;
            const dIdx: number = opts?.dataPointIndex ?? -1;
            const kind = seriesKind[sIdx];
            if (!Number.isFinite(n) || n === this.AC_NULL || n === 0) return '';

            if (kind === 'ac') {
              const solarProduction = this.safeNumber(sorted?.[dIdx]?.solarProductionKwh);

              if (n === this.AC_ON) return '⚡';
              if (n === this.AC_OFF) return solarProduction === 0 ? '' : '🔆';
              return '';
            }

            if (kind === 'chargeDischargeState') {
              return '';
            }

            if (kind === 'profit') {
              if (n >= this.PROFIT_NEG_OFFSET) return '⬇';
              if (n >= this.PROFIT_POS_OFFSET) return '$';
            }

            return '';
          },
          style: { fontSize: '14px', fontWeight: '700', colors: ['#ffffff'] }
        },
        title: {
          text: title,
          align: 'left',
          offsetX: -9,
          style: { fontWeight: '500', fontSize: '15px', color: '#475569' }
        },
        grid: { show: true, strokeDashArray: 5, borderColor: '#e0e0e0' },
        xaxis: {
          type: 'category',
          categories,
          axisBorder: { show: false },
          axisTicks: { show: true },
          labels: { show: true, style: { colors: '#919aa3', fontSize: '14px' } }
        },
        yaxis: {
          labels: { show: true, style: { colors: '#919aa3', fontSize: '14px' } },
          axisBorder: { show: false }
        },
        legend: {
          show: false,
          position: 'right',
          offsetY: 0,
          fontSize: '13px',
          labels: { colors: '#64748b' }
        },
        tooltip: {
          y: {
            formatter: (val: any, opts: any) => {
              const n = Number(val);
              if (!Number.isFinite(n)) return val;

              // No-data sentinel (future/unloaded hours) → always N/A
              if (n === this.AC_NULL) return tNA;

              const sIdx: number = opts?.seriesIndex ?? -1;
              const kind = seriesKind[sIdx];

              switch (kind) {
                case 'ac':
                  if (n === this.AC_ON) return tOn;
                  if (n === this.AC_OFF) return tOff;
                  return tNA;

                case 'chargeDischargeState':
                  if (n === this.CHARGE_STATE_ON)    return tChargeOn;
                  if (n === this.DISCHARGE_STATE_ON) return tDischargeOn;
                  if (n === this.CHARGE_STATE_OFF)   return tOff;
                  return tNA;

                case 'dischargeLimit':
                  return this.formatPercent(n - this.DISCHARGE_OFFSET);

                case 'chargeLimit':
                  return this.formatPercent(n - this.CHARGE_LIMIT_OFFSET);

                case 'soc':
                  return this.formatPercent(n);

                case 'charge': {
                  const pct = this.clamp(n - this.CHARGE_OFFSET, 0, 100);
                  const kwh = (pct * capacityKwh) / 100;
                  return this.formatKwh(kwh);
                }

                case 'dischargeKwh': {
                  const pct = this.clamp(n - this.DISCHARGE_KWH_OFFSET, 0, 100);
                  const kwh = (pct * capacityKwh) / 100;
                  return this.formatKwh(kwh);
                }

                case 'chargeGrid':
                  return this.formatKwh(n - this.CHARGE_FROM_GRID_OFFSET);

                case 'solar':
                  return this.formatKwh(n - this.SOLAR_OFFSET);

                case 'consumption':
                  return this.formatKwh(n - this.CONSUMPTION_OFFSET);

                case 'profit': {
                  if (n >= this.PROFIT_NEG_OFFSET)
                    return this.formatKr(-((n - this.PROFIT_NEG_OFFSET) / profitScale));
                  if (n >= this.PROFIT_POS_OFFSET)
                    return this.formatKr((n - this.PROFIT_POS_OFFSET) / profitScale);
                  return this.formatKr(0);
                }

                default:
                  return n.toFixed(2);
              }
            }
          }
        }
      };

      this.chart = new ApexCharts(host, options);
      this.chart.render();
    } catch (error) {
      console.error('Error loading ApexCharts (BasicHeatmap):', error);
    }
  }

  destroyChart(): void {
    this.langSub?.unsubscribe();
    this.langSub = undefined;

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private normalize1to100(min: number, max: number, n: number): number {
    // Handle edge case where min equals max to avoid division by zero
    if (min === max) {
      max = max + 1;
    }

    // Clamp n to the [min, max] range
    const clamped = Math.min(Math.max(n, min), max);

    // Normalize to 0–100
    return ((clamped - min) / (max - min)) * 100;
  }

}